import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Form submission logic here
    console.log('Form submitted:', formData);
    setSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: PhoneIcon,
      title: 'Telefon',
      details: ['+90 552 229 90 07', '+90 546 718 58 16'],
      description: 'Pazartesi - Cumartesi 09:00 - 18:00'
    },
    {
      icon: EnvelopeIcon,
      title: 'E-posta',
      details: ['info@fielhome.com', 'satis@fielhome.com'],
      description: '24 saat içinde yanıtlıyoruz'
    },
    {
      icon: MapPinIcon,
      title: 'Adres',
      details: ['Mahmudiye, Ankara Cd. No:90', '16400 İnegöl/Bursa'],
      description: 'Showroom ziyareti için randevu alın'
    },
    {
      icon: ClockIcon,
      title: 'Çalışma Saatleri',
      details: ['Pazartesi - Cumartesi: 09:00 - 18:00', 'Pazar: 11:00 - 17:00'],
      description: 'Resmi tatillerde kapalıyız'
    }
  ];

  const faqs = [
    {
      question: 'Teslimat süresi ne kadar?',
      answer: 'Standart ürünler için 2-5 iş günü, özel sipariş ürünler için 3-6 hafta teslimat süresi bulunmaktadır.'
    },
    {
      question: 'Montaj hizmeti veriyor musunuz?',
      answer: 'Evet, tüm mobilyalarımız için profesyonel montaj hizmeti sunuyoruz. Montaj ücreti ürünle birlikte belirtilir.'
    },
    {
      question: 'İade ve değişim şartları neler?',
      answer: '30 gün içinde, orijinal ambalajında ve kullanılmamış durumda olan ürünleri iade edebilirsiniz.'
    },
    {
      question: 'Garanti süresi ne kadar?',
      answer: 'Tüm ürünlerimizde minimum 2 yıl, premium ürünlerde 10 yıla kadar garanti sunuyoruz.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 to-black/30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full mb-8">
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-white" />
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-luxury font-bold text-white mb-6">
              İletişim
            </h1>
            
            <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8">
              Sorularınız, önerileriniz veya özel talepleriniz için bizimle iletişime geçin. 
              Uzman ekibimiz size yardımcı olmak için burada.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-luxury font-bold text-gray-900 mb-6">
              Bize Ulaşın
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Size en uygun iletişim kanalını seçin, en kısa sürede yanıtlayalım
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl border border-gray-100 p-8 text-center hover:shadow-lg transition-shadow duration-300"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-6">
                  <info.icon className="h-8 w-8 text-slate-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {info.title}
                </h3>
                <div className="space-y-2 mb-4">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-900 font-medium">
                      {detail}
                    </p>
                  ))}
                </div>
                <p className="text-sm text-gray-600">
                  {info.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                  Mesaj Gönder
                </h3>
                
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <CheckCircleIcon className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">
                      Mesajınız İletildi!
                    </h4>
                    <p className="text-gray-600">
                      En kısa sürede size geri dönüş yapacağız.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ad Soyad *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500 transition-colors duration-300"
                          placeholder="Adınızı ve soyadınızı girin"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Telefon
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500 transition-colors duration-300"
                          placeholder="Telefon numaranız"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        E-posta *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500 transition-colors duration-300"
                        placeholder="E-posta adresiniz"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Konu
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500 transition-colors duration-300"
                      >
                        <option value="">Konu seçin</option>
                        <option value="genel">Genel Bilgi</option>
                        <option value="urun">Ürün Hakkında</option>
                        <option value="siparis">Sipariş Durumu</option>
                        <option value="teknik">Teknik Destek</option>
                        <option value="iade">İade/Değişim</option>
                        <option value="oneri">Öneri/Şikayet</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mesaj *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500 transition-colors duration-300 resize-none"
                        placeholder="Mesajınızı buraya yazın..."
                      />
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full bg-slate-600 hover:bg-slate-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2"
                    >
                      <EnvelopeIcon className="h-5 w-5" />
                      <span>Mesajı Gönder</span>
                    </button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Map & Location Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Map */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="h-80 relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12197.725!2d29.512!3d40.078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14ca1f00000000%3A0x0!2s%C4%B0neg%C3%B6l%2C%20Bursa%2C%20Turkey!5e0!3m2!1str!2str!4v1703123456789!5m2!1str!2str&q=Mahmudiye,+Ankara+Cd.+No:90,+16400+İnegöl,+Bursa"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="FielHome Showroom Lokasyonu"
                    className="rounded-t-2xl"
                  ></iframe>
                  
                  {/* Map Overlay with Company Info */}
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                    <div className="flex items-center space-x-2">
                      <MapPinIcon className="h-5 w-5 text-slate-600" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900">FielHome Showroom</p>
                        <p className="text-xs text-gray-600">Mahmudiye, İnegöl/Bursa</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    Showroom Lokasyonu
                  </h4>
                  <p className="text-gray-600 mb-4">
                    Mahmudiye, Ankara Cd. No:90<br />
                    16400 İnegöl/Bursa
                  </p>
                  <div className="flex space-x-3">
                    <a
                      href="https://maps.google.com/?q=Mahmudiye,+Ankara+Cd.+No:90,+16400+İnegöl/Bursa"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-slate-700 font-medium transition-colors duration-300"
                    >
                      Google Maps'te Aç →
                    </a>
                    <a
                      href="https://yandex.com.tr/maps/?text=Mahmudiye,+Ankara+Cd.+No:90,+16400+İnegöl/Bursa"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-slate-700 font-medium transition-colors duration-300"
                    >
                      Yandex Maps →
                    </a>
                  </div>
                </div>
              </div>

                             {/* Second Address */}
               <div className="bg-white rounded-2xl shadow-lg p-6">
                 <h4 className="text-lg font-semibold text-gray-900 mb-4">
                   2. Showroom Adresi
                 </h4>
                 <div className="space-y-3">
                   <div className="flex items-start space-x-3">
                     <MapPinIcon className="h-5 w-5 text-slate-600 mt-1" />
                     <div>
                       <p className="font-medium text-gray-900">İkinci Lokasyon</p>
                       <p className="text-gray-600 text-sm">
                         Mahmudiye, Ertuğrul Gazi Cd. No:42<br />
                         16400 İnegöl/Bursa
                       </p>
                     </div>
                   </div>
                   <a
                     href="https://maps.google.com/?q=Mahmudiye,+Ertuğrul+Gazi+Cd.+No:42,+16400+İnegöl/Bursa"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="inline-block text-slate-600 hover:text-slate-700 font-medium text-sm transition-colors duration-300"
                   >
                     Haritada Görüntüle →
                   </a>
                 </div>
               </div>

               {/* Additional Info */}
               <div className="bg-white rounded-2xl shadow-lg p-6">
                 <h4 className="text-lg font-semibold text-gray-900 mb-4">
                   Showroom Ziyareti
                 </h4>
                 <div className="space-y-4">
                   <div className="flex items-start space-x-3">
                     <ClockIcon className="h-5 w-5 text-slate-600 mt-1" />
                     <div>
                       <p className="font-medium text-gray-900">Açılış Saatleri</p>
                       <p className="text-gray-600 text-sm">
                         Pazartesi - Cumartesi: 09:00 - 18:00<br />
                         Pazar: 11:00 - 17:00
                       </p>
                     </div>
                   </div>
                   <div className="flex items-start space-x-3">
                     <PhoneIcon className="h-5 w-5 text-slate-600 mt-1" />
                     <div>
                       <p className="font-medium text-gray-900">Randevu</p>
                       <p className="text-gray-600 text-sm">
                         Özel danışmanlık için önceden randevu almanızı öneriyoruz
                       </p>
                     </div>
                   </div>
                 </div>
               </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-luxury font-bold text-gray-900 mb-6">
              Sık Sorulan Sorular
            </h2>
            <p className="text-xl text-gray-600">
              En çok merak edilen konulara yanıtlar
            </p>
          </motion.div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 rounded-2xl p-6"
              >
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  {faq.question}
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-3xl lg:text-4xl font-luxury font-bold text-white mb-6">
              Yanıtını Bulamadınız mı?
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Bizimle doğrudan iletişime geçin. Uzman ekibimiz tüm sorularınızı yanıtlamaya hazır.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+905522299007"
                className="inline-flex items-center px-8 py-3 bg-white text-slate-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300"
              >
                <PhoneIcon className="h-5 w-5 mr-2" />
                Hemen Ara
              </a>
              <a
                href="mailto:info@fielhome.com"
                className="inline-flex items-center px-8 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-slate-900 transition-colors duration-300"
              >
                <EnvelopeIcon className="h-5 w-5 mr-2" />
                E-posta Gönder
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage; 